<script>
import useVuelidate from '@vuelidate/core';
import {required} from '@vuelidate/validators';

/* COMPOSANTS */
import {Icon} from '@iconify/vue';
import AkInputText from '@components/v2/input/AkInputText';
import AkInputTextArea from '@components/v2/input/AkInputTextArea.vue';
import AkDialog from '@components/general/AkDialog';
import GaView from '@components/v2/layout/GaView';
import GaListView from '@components/v2/layout/GaListView';
import AkDatatablePagination from '@components/v2/general/AkDatatablePagination.vue';
import AkButtonAction from '@components/v2/general/AkButtonAction.vue';
import AkDropdownIcon from '@components/v2/input/AkDropdownIcon.vue';

/* MIXINS */
import randomRef from '@mixins/randomRef';
import utilsMixin from '@mixins/utilsMixin';
import messageMixin from '@mixins/messageMixin';
import iconAutocompleteMixin from '@mixins/autocomplete/iconAutocompleteMixin';

/* SERVICES */
import rentalTagService from '@services/rentalTagService';
import AkColorPicker from '@components/v2/input/AkColorPicker.vue';
import AkButtonValidationV2 from '../../components/v2/general/AkButtonValidationV2.vue';

export default {
  components: {
    AkButtonValidationV2,
    AkColorPicker,
    Icon,
    AkButtonAction,
    AkDatatablePagination,
    AkInputTextArea,
    GaView,
    GaListView,
    AkInputText,
    AkDialog,
    AkDropdownIcon,
  },
  mixins: [randomRef, utilsMixin, messageMixin, iconAutocompleteMixin],
  setup() {
    return {v$: useVuelidate()};
  },
  head() {
    return {
      title: 'settings.rental_tag',
    };
  },
  data() {
    return {
      loading: true,
      list: [],
      item: {
        label: '',
        description: '',
      },
    };
  },
  validations() {
    return {
      item: {
        name: {required},
        color: {required},
      },
    };
  },
  mounted() {
    rentalTagService.rentalTags().then((data) => {
      this.list = data;
      this.loading = false;
    });
  },
  methods: {
    create() {
      this.v$.$touch();
      if (this.v$.$error) {
        this.$refs.dialogCreate.init();
        return;
      }

      // Prendre uniquement le label pour stocker en base
      if (this.item.logo) this.item.logo = this.item.logo.label;

      this.loading = true;
      rentalTagService
        .create(this.item)
        .then((data) => {
          this.success(this.$t('settings.rental_tag_added'));
          this.list.push(data);
        })
        .catch(() => {
          this.addError("Erreur survenue durant l'ajout");
        })
        .finally(() => {
          this.loading = false;
          this.$refs.dialogCreate.hide();
        });
    },
    update() {
      this.v$.$touch();
      if (this.v$.$error) {
        this.$refs.dialogEdit.init();
        return;
      }

      // Prendre uniquement le label pour stocker en base
      if (this.item.logo && this.item.logo.icon) this.item.logo = this.item.logo.label;
      else this.item.logo = null;

      this.loading = true;
      rentalTagService
        .update(this.item)
        .then((data) => {
          this.replaceInListById(this.list, data);
          this.success(this.$t('settings.rental_tag_updated'));
        })
        .catch(() => {
          this.addError('Erreur survenue durant la modification');
        })
        .finally(() => {
          this.loading = false;
          this.$refs.dialogEdit.hide();
        });
    },
    openCreateDialog() {
      this.item = {
        label: '',
        color: '#FFFFFF',
        description: '',
      };
      this.$refs.dialogCreate.show();
    },
    openEditDialog(item) {
      this.item = {...item};
      const logo = this.item.logo;
      this.item.logo = {
        icon: logo,
        label: logo,
      };
      this.$refs.dialogEdit.show();
    },
    launchSynchroTags() {
      rentalTagService.init().then(() => {
        this.$refs.btnInit.init();
      });
    },
  },
};
</script>

<template>
  <GaView :ref="ref" :title="$t('settings.rental_tag')">
    <template #action>
      <div class="flex justify-end gap-2">
        <AkButtonAction little :label="$t('add')" @click="openCreateDialog()" />
        <AkButtonValidationV2
          ref="btnInit"
          btnClass="btn-grey"
          label="Synchronisez les étiquettes"
          @click="launchSynchroTags()" />
      </div>
    </template>

    <template #content>
      <GaListView>
        <template #content>
          <AkDatatablePagination :lazy="false" :loading="loading" :value="list">
            <template #empty>
              {{ $t('settings.rental_tag_empty') }}
            </template>
            <Column header="Nom" field="name">
              <template #body="slotProps">
                <span>{{ slotProps.data.name }}</span>
              </template>
            </Column>
            <Column header="Couleur" field="color">
              <template #body="slotProps">
                <span class="w-6 h-6 rounded block" :style="{'background-color': slotProps.data.color}"></span>
              </template>
            </Column>
            <Column header="Icône" field="logo" style="width: 400px">
              <template #body="slotProps">
                <div v-if="slotProps.data.logo" class="flex items-center">
                  <div :class="`mr-2 fa ${slotProps.data.logo.toLowerCase()}`" style="width: 18px"></div>
                  <div>{{ slotProps.data.logo }}</div>
                </div>
              </template>
            </Column>
            <Column style="width: 100px">
              <template #body="slotProps">
                <div class="flex justify-end gap-2">
                  <span class="md:hover:cursor-pointer" @click="openEditDialog(slotProps.data)">
                    <i class="ga-icon ga-edit text-2xl text-grey-900" />
                  </span>
                </div>
              </template>
            </Column>
          </AkDatatablePagination>
        </template>
      </GaListView>
    </template>

    <template #extra>
      <AkDialog
        ref="dialogCreate"
        :title="$t('settings.rental_tag')"
        :validate-label="$t('add')"
        width="450px"
        @validate="create()">
        <div class="flex flex-col gap-2">
          <AkInputText v-model="item.name" :label="$t('settings.rental_tag_label')" :validator="v$.item.name" />
          <AkColorPicker v-model="item.color" label="Couleur" :validator="v$.item.color" inside-dialog />
          <AkDropdownIcon
            v-model="item.logo"
            :options="this.iconFaConst"
            :option-label="(obj) => obj.label"
            :option-value="(obj) => obj"
            label="Icône"
            filter />
        </div>
      </AkDialog>

      <AkDialog
        ref="dialogEdit"
        :title="$t('settings.rental_tag')"
        :validate-label="$t('update')"
        width="450px"
        @validate="update()">
        <div class="flex flex-col gap-2">
          <AkInputText v-model="item.name" :label="$t('settings.rental_tag_label')" :validator="v$.item.name" />
          <AkColorPicker v-model="item.color" label="Couleur" :validator="v$.item.color" inside-dialog />
          <AkDropdownIcon
            v-model="item.logo"
            :options="this.iconFaConst"
            :option-label="(obj) => obj.label"
            :option-value="(obj) => obj"
            label="Icône"
            filter />
        </div>
      </AkDialog>
    </template>
  </GaView>
</template>
